import { memo, useCallback, useId, type ChangeEvent, type FC } from "react";
import { Plus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import MiniSpinner from "src/components/reusable/Spinner/MiniSpinner";
import ErrorFallback from "src/components/reusable/ErrorFallback/ErrorFallback";
import CampaignStepCard from "../../../../shared/CampaignStepCard";
import CampaignPaginationFooter from "../../../../shared/CampaignPaginationFooter";
import TemplateCard, { CreateOwnTemplateCard } from "../components/TemplateCard";
import type { EmailTemplatesState } from "../hooks/UseEmailTemplates";
import { TEMPLATE_EDITOR_KEY } from "../TemplateEditorConstants";

const STEP_KEY = `${TEMPLATE_EDITOR_KEY}.chooseTemplate`;

interface ChooseTemplateStepProps {
  templatesState: EmailTemplatesState;
  brandName: string;
  renderingTemplateId: string | null;
  onPreview: (_templateId: string) => void;
  onUse: (_templateId: string) => void;
}

const ChooseTemplateStep: FC<ChooseTemplateStepProps> = ({
  templatesState,
  brandName,
  renderingTemplateId,
  onPreview,
  onUse,
}) => {
  const { t } = useTranslation();
  const sectionTitleId = useId();
  const {
    templates,
    hasTemplates,
    blankTemplateId,
    isLoading,
    hasError,
    searchTerm,
    setSearchTerm,
    loadTemplates,
    currentPage,
    totalPages,
    goToPreviousPage,
    goToNextPage,
  } = templatesState;

  const isBusy = Boolean(renderingTemplateId);
  const isBlankApplying = renderingTemplateId === blankTemplateId;

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value),
    [setSearchTerm]
  );

  const handleCreateOwn = useCallback(() => onUse(blankTemplateId), [blankTemplateId, onUse]);

  const renderTemplates = () => {
    if (isLoading) {
      return <MiniSpinner />;
    }
    if (hasError) {
      return <ErrorFallback onRetry={loadTemplates} />;
    }

    return (
      <>
        {hasTemplates && !templates.length && (
          <p className="template-library__empty">{t(`${STEP_KEY}.noResults`)}</p>
        )}
        <div className="template-library__grid">
          {templates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              brandName={brandName}
              isApplying={renderingTemplateId === template.id}
              isDisabled={isBusy}
              onPreview={onPreview}
              onUse={onUse}
            />
          ))}
          <CreateOwnTemplateCard
            isApplying={isBlankApplying}
            isDisabled={isBusy}
            onCreate={handleCreateOwn}
          />
        </div>
      </>
    );
  };

  return (
    <CampaignStepCard
      wide
      title={t(`${STEP_KEY}.title`)}
      subtitle={t(`${STEP_KEY}.subtitle`)}
      footer={
        totalPages > 1 ? (
          <CampaignPaginationFooter
            currentPage={currentPage}
            totalPages={totalPages}
            onPrev={goToPreviousPage}
            onNext={goToNextPage}
          />
        ) : undefined
      }
      headerAction={
        <button
          type="button"
          className="button button--primary campaign-flow__action template-library__create"
          onClick={handleCreateOwn}
          disabled={isBusy}
        >
          <Plus size={14} aria-hidden />
          {t(`${STEP_KEY}.createCampaign`)}
        </button>
      }
    >
      <StyledTextInput
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder={t(`${STEP_KEY}.searchPlaceholder`)}
        prefix={<Search size={16} aria-hidden />}
        className="campaign-flow__input template-library__search"
        aria-label={t(`${STEP_KEY}.searchPlaceholder`)}
      />

      <section className="template-library" aria-labelledby={sectionTitleId}>
        <div className="template-library__heading">
          <h3 id={sectionTitleId} className="template-library__title">
            {t(`${STEP_KEY}.sectionTitle`)}
          </h3>
          <p className="template-library__subtitle">{t(`${STEP_KEY}.sectionSubtitle`)}</p>
        </div>
        {renderTemplates()}
      </section>
    </CampaignStepCard>
  );
};

export default memo(ChooseTemplateStep);
